/**
 * 이상한 방 - 소리 사전
 *
 * 근거 문서: 컨셉시트 4.3절(오답 시 **탄피 사운드**) · 4.5절(점수) · 3절(라운드 구조)
 *           디자인 시안 A - 현장 감식 × 자막 문법(2026-08-31 "온에어"에서 2026-09-02 교체)
 *
 * ## 톤: 현장 장비지 오케스트라가 아니다
 *
 * 화면이 현장 표식과 자막 플레이트이므로 소리도 그 세계의 것이어야 한다. 감식 가방에서
 * 나는 것들 - **짧은 비프, 도장, 셔터, 테이프 감기**. 팡파레·박수·환호는 넣지 않는다.
 * 그건 다른 게임의 소리이고, 무엇보다 **길다.** 5분에 12번 울리는 소리가 1초를 넘으면
 * 그 자체가 소음이 된다.
 *
 * "온에어" 시절에는 같은 넷을 부조정실의 소리(비프·스탬프·셔터·릴 감기)라고 불렀다.
 * **합성은 한 줄도 바뀌지 않았다** - 같은 소리가 다른 세계의 물건으로 읽힐 뿐이다.
 *
 * 그래서 대부분의 소리가 0.2초 이내다. 유일한 예외가 `perfect`인데, 그건 라운드가 끝난 뒤
 * 한 번만 울리는 소리라 길이를 쓸 자격이 있다.
 *
 * ## 콤보 피치가 이 파일의 존재 이유다
 *
 * `hit`은 콤보 단계를 받아 **반음씩 올라간다**. 이 게임의 유일한 보상이 "찾았다"이고,
 * 그 순간이 매번 똑같은 소리면 12번째 발견이 첫 번째와 같은 값어치가 된다.
 * 올라가는 음은 "쌓이고 있다"를 **점수판을 보지 않고도** 알려 주는 유일한 장치다.
 *
 * 상한(`hit.maxStep`)이 있는 이유는 귀다. 한 라운드에 12번 맞히면 무제한일 때 마지막 음이
 * 3.5kHz를 넘어가는데, 그 대역은 즐거움이 아니라 통증이다.
 *
 * ## 개발자가 만질 곳 - `AUDIO_TUNING` 하나
 *
 * 소리마다 `{ hz, sec, gain }` 세 개가 성격의 90%를 정한다.
 *
 * | 필드 | 올리면 | 내리면 |
 * |---|---|---|
 * | `hz`   | 밝고 가벼워진다(작은 물건) | 어둡고 무거워진다(큰 물건) |
 * | `sec`  | 여운이 남는다(느슨함) | 딱 끊긴다(타이트함) |
 * | `gain` | 커진다. 겹이 동시에 정점을 치면 깨지므로 상한이 있다(아래) | 작아진다 |
 * | `masterTrim` | 게임 전체가 커진다 | 게임 전체가 작아진다 |
 *
 * `gain`이 1을 넘는 소리가 있는 것은 오타가 아니다. 이 값은 절대 크기가 아니라 **겹 게인의
 * 배수**이고, 겹이 서로 다른 시각에 울리면(아르페지오·탄피) 실제 정점은 합보다 훨씬 낮다.
 * 진짜 상한은 `sounds.test.ts`가 지킨다 - **같은 순간에 겹치는 겹들의 합 × `gain` ≤ 1**이다.
 * 실측 정점은 기본 볼륨에서 0.03(`uiTap`) ~ 0.38(`perfect`) 사이에 있다.
 *
 * 값은 `as const`가 아니다. **일부러 그렇게 뒀다** - 콘솔에서 숫자를 바꾸고 바로 다시 쏴서
 * 들어 볼 수 있어야 귀로 튜닝이 된다. 소리는 호출할 때마다 이 표를 다시 읽어 조립된다.
 * 개발 모드에서는 파일 맨 아래의 `__wr` 손잡이가 그 통로다.
 */

import { audio, type FilterKind, type SoundSpec, type Voice, type Wave } from './engine'

/* --- 튜닝 표 --------------------------------------------------------------------- */

/** 열두 반음이 한 옥타브. 콤보 한 단계 = 반음 하나. */
const SEMITONE_RATIO = 2 ** (1 / 12)

export const AUDIO_TUNING = {
  /** 사전 전체의 트림. 소리끼리의 균형은 그대로 두고 크기만 바꾼다. */
  masterTrim: 1,

  /** 발견. 밝은 자막 노랑에 해당하는 대역(A5). `maxStep`은 콤보 피치 상한(반음 수)이다. */
  hit: { hz: 880, sec: 0.11, gain: 0.94, maxStep: 12 },
  /** 오답. 낮고 둔탁한 몸통 + 바닥에 떨어지는 탄피(`shellHz`가 탄피의 금속 대역). */
  miss: { hz: 150, sec: 0.16, gain: 1, shellHz: 4200 },
  /** 이미 찾은 곳. 오답과 달라야 한다 - 벌이 아니라 "아무 일도 없음"이다. */
  alreadyFound: { hz: 466, sec: 0.055, gain: 0.58 },
  /** 탄창. 철컥. `hz`는 두 번째 클랙의 금속 대역이다. */
  reload: { hz: 2600, sec: 0.045, gain: 1 },
  /** 원본 방으로 들어간다. 스윕의 시작 대역. */
  warpIn: { hz: 5200, sec: 0.34, gain: 0.68 },
  /** 변경본으로 돌아온다. 스윕의 도착 대역. */
  warpOut: { hz: 5200, sec: 0.3, gain: 0.68 },
  /** 잔탄 경고. C6. 높게 두는 이유는 방 소리에 묻히면 경고가 아니기 때문이다. */
  lowAmmo: { hz: 1046, sec: 0.06, gain: 0.85 },
  /** 페이즈 전환 스팅어. D5에서 4도 위로. */
  phaseChange: { hz: 587, sec: 0.2, gain: 0.94 },
  /** 라운드 성공. E5부터 올라간다. */
  roundClear: { hz: 659, sec: 0.26, gain: 1 },
  /** 라운드 실패. A4부터 내려간다. */
  roundFail: { hz: 440, sec: 0.34, gain: 0.85 },
  /**
   * 무오답 전량 발견. C5부터 두 옥타브. 이 게임에서 가장 큰 소리다.
   * 겹이 일곱이지만 다섯은 시차를 두고 울린다. 마지막 세 겹이 겹치는 0.34초가 정점이고,
   * 여기가 1을 넘으면 깨진다 - 게인을 올릴 때 볼 곳은 그 지점이다(`sounds.test.ts`).
   */
  perfect: { hz: 523, sec: 0.42, gain: 1.19 },
  /** 리빌 공개. 고무 스탬프가 종이를 때리는 대역. */
  reveal: { hz: 760, sec: 0.06, gain: 0.94 },
  /** 버튼. 아주 짧고 작게. 이 소리가 들리면 이미 너무 큰 것이다. */
  uiTap: { hz: 2100, sec: 0.016, gain: 0.51 },
  /**
   * 남이 찾았다(여럿이). 발견음과 같은 대역·같은 겹이되 **작고 짧다** - 정보이지 내 보상이 아니다.
   * `uiTap`보다는 커야 한다(가장 작은 소리는 버튼이다 - `sounds.test.ts`가 지킨다).
   */
  someoneFound: { hz: 880, sec: 0.07, gain: 0.55 },
  /**
   * 늦었다(여럿이 `sniped`). 같은 곳을 남이 먼저 찍어 **손해 0**인 한 발. 중복 사격(`alreadyFound`)과 같은
   * 성격("아무 일도 없음")이되 한 번 내려간다 - 반복이 아니라 "지나갔다"다. 오답보다 높고 조용해야 한다.
   */
  sniped: { hz: 392, sec: 0.08, gain: 0.5 },
  /**
   * 순위 공개(여럿이 결과). 리빌 도장(`reveal`)을 세 번, 조금씩 올려 찍는다 - 성공(`roundClear`)도 실패(`roundFail`)도
   * 아닌 "판이 끝나고 표가 붙었다"다. 라운드 밖 소리라 길어도 되지만 `perfect`보다 짧고 작다.
   */
  ranking: { hz: 760, sec: 0.08, gain: 0.8 },
  /**
   * 바꾸기 수용(여럿이 M4, 사람 체인저). 가벼운 '툭' - 물건을 내려놓는 소리다. 60초에 열두 번 울리므로 `uiTap`보다는
   * 있되 발견음(`hit`)보다 작고 짧아야 한다. 음정을 거의 죽인 이유는 이것이 보상이 아니라 **확인**이기 때문이다 -
   * 올라가는 음은 찾는 사람의 것이다.
   */
  changeAccepted: { hz: 520, sec: 0.055, gain: 0.62 },
  /**
   * 문이 비었다(여럿이 M5 - 원래 방 문 앞 줄). 줄 선 사람에게 "이제 들어갈 수 있다"를 알리는 소리라 **위로 여는** 두 음이되
   * 발견음(`hit`)과 겹치면 안 된다 - 대역을 낮게 두고(`hz` E5 아래) 사각파를 쓰지 않는다. 문 걸쇠가 풀리는 짧은 노이즈 한 겹이
   * 앞에 붙어 "문"으로 읽힌다. 5초 유예 안에 눌러야 하는 신호라 `lowAmmo`처럼 뚫고 나와야 하지만 그보다 조용하다 - 경고가 아니다.
   */
  doorOpen: { hz: 440, sec: 0.12, gain: 0.72 },

  /*
   * 봉투 아홉(로드맵 M3 3.3절 · P5). 전부 라운드 밖(단계 선택 화면)에서만 울리므로 0.2초 규칙의 대상이 아니지만,
   * `perfect`(라운드의 가장 큰 보상음)보다 길거나 크지는 않는다 - 봉투가 판보다 요란하면 게임의 무게가 뒤집힌다.
   *
   * 아홉 중 **둘(`envelopeRiser`·`envelopeImpact`)은 SR·SSR에서만 울린다.** 아래 등급이 조용한 것이
   * 위 등급의 값어치이므로(`ui/envelopeCeremony.ts` 머리말), 그 둘을 N·R로 내리는 변경은 소리 쪽에서
   * 같은 계단을 무너뜨리는 일이다.
   */
  /** 봉투가 올라온다. 종이가 스치는 로우패스 스윕. */
  envelopeOpen: { hz: 1800, sec: 0.2, gain: 0.7 },
  /** 봉인이 찢긴다. 하이패스 노이즈 한 겹 + 짧은 갈라짐. */
  envelopeTear: { hz: 2400, sec: 0.14, gain: 0.85 },
  /** 카드가 뒤집힌다. 종이 한 장이 공기를 치는 소리. */
  cardFlip: { hz: 520, sec: 0.06, gain: 0.8 },
  /** SR. G5에서 5도 위로 한 번 열린다. */
  gradeRare: { hz: 784, sec: 0.3, gain: 0.9 },
  /** SSR. `perfect`와 같은 C 장조 아르페지오지만 한 옥타브 안에서 끝난다 - 판의 보상음보다 작아야 한다. */
  gradeLegend: { hz: 523, sec: 0.36, gain: 0.95 },
  /**
   * 개봉 전의 차오름. 반음씩 오르는 짧은 틱 일곱이 봉투의 떨림에 실린다. **등급을 모른다** -
   * 네 등급이 같은 소리를 듣는 것이 이 화면의 규칙이다(`ui/envelopeCeremony.ts` 머리말).
   *
   * 길이는 0.71초이고, 여기서 지켜야 하는 제약은 그것 하나다 - **`perfect`(0.76초)보다 짧다.**
   * 봉투가 판보다 요란하면 게임의 무게가 뒤집히기 때문이고, 그 규칙은 `sounds.test.ts`가 지킨다.
   *
   * **언제 울리는가는 여기가 정하지 않는다.** 화면이 `envelopeChargeSec()`으로 길이를 물어 **릴이 멎는
   * 순간에 끝나도록 역산해** 앉힌다(시각표는 `ui/envelopePhases.ts`, 그 결정의 이유는
   * `ui/envelopeReelSound.ts` 머리말). 그러니 이 주석에 시작 시각을 적지 마라 - 릴 길이가 바뀌면
   * 그 숫자만 옛말이 되고, 실제로 한 번 그렇게 됐다.
   */
  envelopeCharge: { hz: 392, sec: 0.05, gain: 0.62 },
  /** SSR의 금빛이 터진다. 낮은 몸통 + 위로 열리는 광채. `gradeLegend` 위에 한 겹 더 쌓인다. */
  envelopeBurst: { hz: 262, sec: 0.42, gain: 0.9 },
  /**
   * SR·SSR의 개봉 **직전**(어두워지는 구간)에 오르는 상승음. `hz`가 출발 대역이고 끝에서 세 옥타브 위까지 간다.
   *
   * `envelopeCharge`(개봉 전의 차오름)와 다른 소리인 이유는 아는 것이 다르기 때문이다 - 차오름은 등급을
   * 모르고 네 등급이 똑같이 듣지만, 이것은 어둠과 함께 오는 소리라 **이미 등급이 정해진 뒤**의 것이다.
   * 그 둘을 한 소리로 합치면 차오름이 등급을 아는 소리가 되고, 그 순간 개봉 전이 스포일러가 된다.
   */
  envelopeRiser: { hz: 220, sec: 0.4, gain: 0.66 },
  /**
   * 카드가 꽂히는 순간의 저역 타격. 몸으로 치는 소리라 대역이 가장 낮다(`miss`의 150보다 아래).
   *
   * SR은 이것 하나, SSR은 `envelopeBurst`와 겹쳐 같은 순간에 둘, 스탬프에서 `gradeLegend`까지 세 겹이 된다 -
   * **겹의 수가 곧 등급**인 것이 이 화면의 소리 규칙이다(SSR만 두 겹이던 것을 P5가 한 겹 더 늘렸다).
   */
  envelopeImpact: { hz: 96, sec: 0.34, gain: 0.88 },
  /**
   * 릴이 확률 띠의 경계 하나를 지난다. 한 회차에 열몇 번 울리므로 **작고 마른** 소리여야 한다 -
   * `uiTap` 바로 위에 두고(가장 작은 소리는 버튼이라는 규칙은 지킨다) 음정을 하나만 쓴다.
   *
   * 같은 소리를 간격만 바꿔 반복하는 형태다. 릴이 감속하면 간격이 저절로 벌어지므로 소리를 여러 개
   * 만들 필요가 없고, "음이 올라가는" 쪽은 마지막 접근에 겹치는 `envelopeCharge`가 맡는다
   * (`ui/envelopePhases.ts` 시각표).
   */
  reelTick: { hz: 1400, sec: 0.03, gain: 0.42 },
}

export type AudioTuning = typeof AUDIO_TUNING

/* --- 겹 만들기 도우미 ------------------------------------------------------------- */

/** 기본 파형. 삼각파는 배음이 적어 겹을 여럿 쌓아도 뭉개지지 않는다. */
const DEFAULT_WAVE: Wave = 'triangle'

type ToneOptions = Readonly<{
  wave?: Wave
  hzTo?: number
  delay?: number
  attack?: number
  /** 있으면 이 대역으로 깎는다. 종류를 안 적으면 로우패스(광채를 죽이는 쪽)다. */
  filterHz?: number
  filterKind?: FilterKind
}>

function tone(hz: number, decay: number, gain: number, options: ToneOptions = {}): Voice {
  const glide = options.hzTo === undefined ? {} : { hzTo: options.hzTo }

  return {
    source: { kind: 'tone', wave: options.wave ?? DEFAULT_WAVE, hz, ...glide },
    decay,
    gain,
    ...(options.delay === undefined ? {} : { delay: options.delay }),
    ...(options.attack === undefined ? {} : { attack: options.attack }),
    ...(options.filterHz === undefined
      ? {}
      : { filter: { kind: options.filterKind ?? 'lowpass', hz: options.filterHz } }),
  }
}

type NoiseOptions = Readonly<{
  /** 안 적으면 밴드패스다. 노이즈를 "물건"으로 만드는 것이 좁은 대역이기 때문이다. */
  kind?: FilterKind
  hzTo?: number
  q?: number
  delay?: number
  attack?: number
}>

function noise(filterHz: number, decay: number, gain: number, options: NoiseOptions = {}): Voice {
  return {
    source: { kind: 'noise' },
    decay,
    gain,
    filter: {
      kind: options.kind ?? 'bandpass',
      hz: filterHz,
      ...(options.hzTo === undefined ? {} : { hzTo: options.hzTo }),
      ...(options.q === undefined ? {} : { q: options.q }),
    },
    ...(options.delay === undefined ? {} : { delay: options.delay }),
    ...(options.attack === undefined ? {} : { attack: options.attack }),
  }
}

function spec(id: string, gain: number, voices: readonly Voice[]): SoundSpec {
  return { id, gain: gain * AUDIO_TUNING.masterTrim, voices }
}

/* --- 콤보 피치 ------------------------------------------------------------------- */

/**
 * 콤보 단계 → 실제로 올릴 반음 수.
 *
 * 음수·NaN을 0으로 접는 이유는 호출부다. 콤보는 게임 상태에서 오고, 그 값이 잠깐 이상해도
 * **소리가 던져서 사격이 멈추면 안 된다.** 소리는 부가 기능이다.
 */
export function comboSemitones(step: number): number {
  if (!Number.isFinite(step) || step <= 0) return 0
  return Math.min(Math.floor(step), AUDIO_TUNING.hit.maxStep)
}

/** 기준 주파수에 콤보 단계만큼 반음을 얹는다. */
export function comboHz(baseHz: number, step: number): number {
  return baseHz * SEMITONE_RATIO ** comboSemitones(step)
}

/* --- 사전 ----------------------------------------------------------------------- */

/**
 * 발견. **이 게임의 유일한 보상음이다.**
 *
 * 겹이 셋인 이유가 "자막 스탬프"라는 그림에 그대로 대응한다.
 *  - 사각파 본음: 자막의 노랑. 현장 장비 비프의 성격이 여기서 나온다. **음정이 고정이라야**
 *    반음 상승이 읽힌다. 그래서 이 겹에는 글라이드를 넣지 않았다.
 *  - 옥타브 위 삼각파: 자막의 흰 반짝임. 짧게 얹어 "팡"의 밝기를 만든다.
 *  - 하이패스 노이즈 30ms: 스탬프가 찍히는 소리. 이게 없으면 삐 소리이지 타격이 아니다.
 */
export function hit(comboStep = 0): SoundSpec {
  const { hz, sec, gain } = AUDIO_TUNING.hit
  const f = comboHz(hz, comboStep)
  return spec('hit', gain, [
    tone(f, sec, 0.34, { wave: 'square' }),
    tone(f * 2, sec * 0.55, 0.16, { wave: 'triangle' }),
    noise(3200, 0.028, 0.12, { kind: 'highpass' }),
  ])
}

/**
 * 오답. 컨셉시트 4.3절이 이름으로 지정한 유일한 소리다.
 *
 *  - 몸통: 사인파가 한 옥타브 **아래로** 떨어진다. 하강 글라이드는 만국 공통으로 "꺼짐"이고,
 *    발견음이 올라가는 것과 정확히 반대라 두 결과가 절대 헷갈리지 않는다.
 *  - 탄피 셋: 좁은 밴드패스(Q 9)를 통과한 30ms 노이즈. Q가 좁아야 노이즈가 금속처럼 운다.
 *    간격이 55 → 60 → 45ms로 **점점 촘촘해지는** 것이 바닥에 튀는 물건의 물리다.
 *    주파수를 조금씩 흔든 것도 같은 이유 - 세 번 다 같은 음이면 튀는 게 아니라 반복이다.
 */
export function miss(): SoundSpec {
  const { hz, sec, gain, shellHz } = AUDIO_TUNING.miss
  return spec('miss', gain, [
    tone(hz, sec, 0.42, { wave: 'sine', hzTo: hz * 0.5 }),
    noise(shellHz, 0.035, 0.16, { q: 9, delay: 0.055 }),
    noise(shellHz * 1.18, 0.03, 0.1, { q: 9, delay: 0.115 }),
    noise(shellHz * 0.92, 0.024, 0.06, { q: 9, delay: 0.16 }),
  ])
}

/**
 * 이미 찾은 곳을 다시 쐈다. 리듀서의 `shot-blocked` 전부(쿨타임 거절·탄창 거절 포함)가 이 소리다.
 *
 * **오답과 반드시 달라야 한다.** 탄환을 먹지 않았는데 오답 소리가 나면 사람은 손해를 봤다고
 * 믿고, 그 억울함이 이 게임에서 가장 나쁜 종류의 오해다(`hunt.ts`가 결과를 셋으로 나눈 이유).
 *
 * 그래서 **음정도 없고 하강도 없다.** 같은 음을 두 번 두드리는 "톡톡"이다. 반복은 새 정보가
 * 아니라는 뜻이고, 로우패스로 광채를 깎아 발견음의 밝기와도 겹치지 않는다.
 */
export function alreadyFound(): SoundSpec {
  const { hz, sec, gain } = AUDIO_TUNING.alreadyFound
  return spec('alreadyFound', gain, [
    tone(hz, sec, 0.2, { filterHz: 1600 }),
    tone(hz, sec, 0.15, { delay: 0.085, filterHz: 1600 }),
  ])
}

/**
 * 탄창. 철컥.
 *
 * 클랙 두 번(당기고 놓는다) + 낮은 몸통. 두 클랙의 대역을 다르게 둔 것이 핵심이다 -
 * 같으면 그냥 노이즈가 두 번이고, 다르면 **금속 부품 두 개**로 들린다.
 * 뒤쪽이 더 높고 큰 이유는 탄창이 물리는 순간이 그쪽이기 때문이다.
 */
export function reload(): SoundSpec {
  const { hz, sec, gain } = AUDIO_TUNING.reload
  return spec('reload', gain, [
    noise(hz * 0.68, 0.028, 0.3, { q: 2.2 }),
    noise(hz, sec, 0.34, { q: 3, delay: 0.085 }),
    tone(95, 0.07, 0.16, { wave: 'sine', delay: 0.085 }),
  ])
}

/**
 * 원본 방으로 들어간다. **하강 스윕** - 화면이 원본 자료로 닫히는 느낌(`gameHud.css` "시각 언어").
 *
 * 로우패스가 5.2kHz에서 280Hz로 내려가면 소리가 문 뒤로 사라지는 것처럼 들린다.
 * 사인파 하강이 그 위에 얹혀 방향(들어간다)을 확실히 한다.
 * 길이를 `warpDissolveSeconds`(0.4초)에 맞춘 것은 소리가 화면보다 먼저 끝나면
 * 전환이 끊긴 것처럼 보이기 때문이다.
 */
export function warpIn(): SoundSpec {
  const { hz, sec, gain } = AUDIO_TUNING.warpIn
  return spec('warpIn', gain, [
    noise(hz, sec, 0.2, { kind: 'lowpass', hzTo: 280 }),
    tone(620, sec * 0.8, 0.18, { wave: 'sine', hzTo: 190 }),
  ])
}

/** 변경본으로 돌아온다. `warpIn`을 뒤집은 **상승 스윕** - 화면이 다시 열린다. */
export function warpOut(): SoundSpec {
  const { hz, sec, gain } = AUDIO_TUNING.warpOut
  return spec('warpOut', gain, [
    noise(280, sec, 0.2, { kind: 'lowpass', hzTo: hz }),
    tone(190, sec * 0.8, 0.18, { wave: 'sine', hzTo: 620 }),
  ])
}

/**
 * 잔탄 경고. 짧은 비프 두 번, 두 번째가 **낮다**.
 *
 * 하강 2음은 장비가 내는 "이상 신호"의 관습이다(엘리베이터·의료기기). 상승으로 만들면
 * 발견음과 방향이 같아져, 가장 나쁜 순간에 가장 좋은 소식처럼 들린다.
 * 사각파인 이유는 방 소리에 묻히지 않기 위해서다 - 홀수 배음이 많아 작아도 뚫고 나온다.
 */
export function lowAmmo(): SoundSpec {
  const { hz, sec, gain } = AUDIO_TUNING.lowAmmo
  return spec('lowAmmo', gain, [
    tone(hz, sec, 0.22, { wave: 'square' }),
    tone(hz * 0.84, sec, 0.22, { wave: 'square', delay: 0.12 }),
  ])
}

/**
 * 페이즈 전환. 짧은 스팅어.
 *
 * 4도 상승 2음(D5 → A5). 3음 이상으로 늘리면 멜로디가 되고, 멜로디는 라운드에 세 번씩
 * 울리기에 너무 개성이 강하다. 2음은 신호로 남는다.
 * 하이패스 노이즈 한 겹이 앞에 붙어 "현장 사진의 플래시가 터지는" 순간의 공기를 만든다.
 */
export function phaseChange(): SoundSpec {
  const { hz, sec, gain } = AUDIO_TUNING.phaseChange
  return spec('phaseChange', gain, [
    tone(hz, 0.12, 0.24, { wave: 'square' }),
    tone(hz * 1.5, sec, 0.26, { wave: 'square', delay: 0.1 }),
    noise(4000, 0.05, 0.1, { kind: 'highpass' }),
  ])
}

/**
 * 라운드 성공. 상승 3음(E5 → A5 → E6).
 *
 * 마지막 음만 사각파로 바꿔 끝을 밝게 연다. 삼각파로 끝내면 "끝났다"이고,
 * 사각파로 끝내면 "다음"이다. 결과 화면에 "한 판 더" 버튼이 있으므로 후자여야 한다.
 */
export function roundClear(): SoundSpec {
  const { hz, sec, gain } = AUDIO_TUNING.roundClear
  return spec('roundClear', gain, [
    tone(hz, 0.18, 0.26),
    tone(hz * 1.335, 0.18, 0.26, { delay: 0.1 }),
    tone(hz * 2, sec, 0.22, { wave: 'square', delay: 0.2 }),
  ])
}

/**
 * 라운드 실패. 하강 3음(A4 → F4 → C4).
 *
 * 장조 3화음을 거꾸로 내려온다. 불협으로 만들면 "고장"으로 들리는데, 이건 고장이 아니라
 * 못 찾은 것이다. 로우패스로 광채를 계속 깎아 내려가면서 **꺼지는** 느낌을 만든다.
 * 성공음보다 길고 게인이 낮은 것도 같은 의도다 - 실패는 조용히 끝나야 한다.
 */
export function roundFail(): SoundSpec {
  const { hz, sec, gain } = AUDIO_TUNING.roundFail
  return spec('roundFail', gain, [
    tone(hz, 0.2, 0.24, { hzTo: hz * 0.94, filterHz: 1200 }),
    tone(hz * 0.794, 0.22, 0.22, { delay: 0.13, filterHz: 1100 }),
    tone(hz * 0.595, sec, 0.2, { delay: 0.26, filterHz: 900 }),
  ])
}

/**
 * 무오답 전량 발견. **이 게임에서 가장 큰 보상음이다.**
 *
 * 유일하게 0.2초 규칙을 어긴다(약 0.76초). 어겨도 되는 이유는 라운드가 이미 끝난 뒤이고,
 * 다음 이상한 곳을 찾을 필요가 없기 때문이다. 화면을 가리지 말라는 제약은 **연출**의 것이지
 * 소리의 것이 아니다.
 *
 * C 장조 아르페지오 두 옥타브(C5 E5 G5 C6 E6). 뒤 두 음을 사각파로 바꿔 정상에서
 * 소리가 열리게 하고, 낮은 C3와 하이패스 반짝임을 같은 시각에 얹어 폭을 준다.
 * 위아래가 동시에 벌어지는 것이 "가장 크다"를 게인을 올리지 않고 만드는 방법이다.
 */
export function perfect(): SoundSpec {
  const { hz, sec, gain } = AUDIO_TUNING.perfect
  return spec('perfect', gain, [
    tone(hz, 0.16, 0.2),
    tone(hz * 1.26, 0.16, 0.2, { delay: 0.08 }),
    tone(hz * 1.5, 0.16, 0.2, { delay: 0.16 }),
    tone(hz * 2, 0.28, 0.24, { wave: 'square', delay: 0.24 }),
    tone(hz * 2.52, sec, 0.28, { wave: 'square', delay: 0.34 }),
    tone(hz * 0.25, sec, 0.18, { wave: 'sine', delay: 0.34 }),
    noise(6000, 0.3, 0.1, { kind: 'highpass', delay: 0.34, attack: 0.02 }),
  ])
}

/**
 * 리빌에서 이상한 곳 하나가 공개된다. **고무 스탬프**다.
 *
 * 화면에서 노란 자막이 기울어진 채 찍히는 그 순간의 소리이므로, 종이를 때리는 저역 노이즈가
 * 주인공이고 음정은 몸통 역할만 한다. 2.5초마다 열두 번 울리므로 밝은 음을 넣으면
 * 클라이맥스가 알람 소리가 된다. 그래서 고역은 3cs짜리 한 겹으로만 스친다.
 */
export function reveal(): SoundSpec {
  const { hz, sec, gain } = AUDIO_TUNING.reveal
  return spec('reveal', gain, [
    noise(hz, sec, 0.32, { q: 1.2 }),
    tone(190, 0.09, 0.24, { wave: 'sine', hzTo: 120 }),
    tone(hz * 1.9, 0.028, 0.1),
  ])
}

/**
 * 남이 찾았다(여럿이, 로드맵 M2). 발견음의 **작은 사촌**이다 - 사각파 본음 하나에 스탬프 노이즈만 얹고,
 * 옥타브 위의 반짝임은 뺐다. 반짝임은 "내 것"의 밝기라 남의 발견에 얹으면 내 콤보가 오른 것처럼 들린다.
 * 콤보 인자를 받지 않는다 - 남의 콤보 단계는 뷰에 오지 않고(`PublicPlayer`에 콤보가 없다), 알아도 내 화면의
 * 음정을 남의 성적으로 움직일 이유가 없다. 늘 같은 음이어야 "누가 하나 찍었다"로만 읽힌다.
 */
export function someoneFound(): SoundSpec {
  const { hz, sec, gain } = AUDIO_TUNING.someoneFound
  return spec('someoneFound', gain, [
    tone(hz, sec, 0.3, { wave: 'square', filterHz: 2400 }),
    noise(3200, 0.02, 0.09, { kind: 'highpass' }),
  ])
}

/**
 * 늦었다(여럿이 `sniped`). 두 번 두드리되 **두 번째가 한 단 낮다**(G4 → E4쯤).
 *
 * `alreadyFound`("톡톡", 같은 음)와 구별되어야 한다 - 저건 내가 이미 찍은 곳이고 이건 남이 방금 가져간 곳이다.
 * 둘 다 탄환을 먹지 않으므로 오답(`miss`, 탄피)과는 절대 겹치면 안 되고, 그래서 탄피 노이즈가 없고 대역도 로우패스로
 * 죽였다. 하강은 한 단만이다 - `roundFail`처럼 세 음을 내려오면 판이 끝난 것으로 들린다.
 */
export function sniped(): SoundSpec {
  const { hz, sec, gain } = AUDIO_TUNING.sniped
  return spec('sniped', gain, [
    tone(hz, sec, 0.22, { filterHz: 1400 }),
    tone(hz * 0.84, sec, 0.16, { delay: 0.07, filterHz: 1400 }),
  ])
}

/**
 * 순위 공개(여럿이 결과). 리빌 도장(`reveal`)의 몸통을 **세 번**, 조금씩 높여 찍는다 - 순위표의 줄이 위에서부터
 * 붙는 그림이다. 방향은 성공(`roundClear`)과 같은 상승이되 3화음이 아니라 도장 소리라, 이긴 사람에게도 진 사람에게도
 * "표가 붙었다"로만 들린다. 여럿이의 라운드 끝은 성공·실패가 아니라 순위 공개이기 때문이다(`matchSessionEffects.ts`).
 * 세 도장이 겹치지 않게 간격(0.14초)이 감쇠(0.08초)보다 길다 - 정점은 도장 하나의 크기다.
 */
export function ranking(): SoundSpec {
  const { hz, sec, gain } = AUDIO_TUNING.ranking
  const stamp = (at: number, pitch: number, tail: number): readonly Voice[] => [
    noise(hz * pitch, sec, 0.3, { q: 1.2, delay: at }),
    tone(190 * pitch, tail, 0.22, { wave: 'sine', hzTo: 120 * pitch, delay: at }),
  ]
  return spec('ranking', gain, [...stamp(0, 1, 0.09), ...stamp(0.14, 1.12, 0.09), ...stamp(0.28, 1.26, 0.16)])
}

/**
 * 바꾸기 수용(여럿이 M4). 사인파 몸통이 한 단 내려앉는 '툭' + 표면에 닿는 짧은 노이즈. 하강은 `sniped`처럼
 * "지나갔다"가 아니라 물건이 자리에 놓이는 무게다 - 그래서 한 단이 아니라 살짝(0.85)이고 필터로 광채를 죽였다.
 * 탄피(`miss`)와 절대 겹치면 안 되므로 노이즈는 로우패스이고 4kHz대의 금속 대역이 없다.
 */
export function changeAccepted(): SoundSpec {
  const { hz, sec, gain } = AUDIO_TUNING.changeAccepted
  return spec('changeAccepted', gain, [
    tone(hz, sec, 0.3, { wave: 'sine', hzTo: hz * 0.85, filterHz: 1600 }),
    noise(900, 0.03, 0.12, { kind: 'lowpass' }),
  ])
}

/**
 * 버튼. 16ms짜리 하이패스 클릭 하나 + 아주 작은 음정.
 *
 * 게임 중에 가장 자주 울리는 소리라 **들리면 안 되고 느껴져야 한다.**
 * 길이를 올리면 즉시 싸구려가 되므로 `sec`은 거의 만지지 않는 값이다.
 */
export function uiTap(): SoundSpec {
  const { hz, sec, gain } = AUDIO_TUNING.uiTap
  return spec('uiTap', gain, [
    noise(5000, 0.012, 0.09, { kind: 'highpass' }),
    tone(hz, sec, 0.06),
  ])
}

/**
 * 문이 비었다(여럿이 M5). 걸쇠 노이즈 → A4 → 4도 위(D5)로 열리는 두 음.
 *
 * 방향이 위인 이유는 잔탄 경고(`lowAmmo`, 하강)와 반대여야 하기 때문이다 - 줄 머리에게 오는 이 신호는 나쁜 소식이 아니다.
 * 그런데 발견음(`hit`, 사각파 A5)과도 갈려야 해서 한 옥타브 아래의 삼각파로 둔다. 걸쇠 노이즈는 좁은 밴드패스(Q 4)라
 * 탄피(`miss`)의 금속 대역(4.2kHz)에 닿지 않는다 - 탄환을 먹지 않은 소리가 탄피 소리를 내면 안 된다.
 */
export function doorOpen(): SoundSpec {
  const { hz, sec, gain } = AUDIO_TUNING.doorOpen
  return spec('doorOpen', gain, [
    noise(1400, 0.03, 0.14, { q: 4 }),
    tone(hz, sec * 0.7, 0.24, { delay: 0.03 }),
    tone(hz * 1.335, sec, 0.26, { delay: 0.11 }),
  ])
}

/* --- 봉투 ----------------------------------------------------------------------- */

/*
 * 봉투 연출(`ui/envelope.ts`)의 소리 일곱. 로드맵 3.3절이 "소리는 전부 합성"이라고 못박았고,
 * 그 이유는 이 파일 머리말과 같다(에셋 0 · 실패는 `false`). 봉투는 단계 선택 화면에서만 열리므로
 * 수색 중 소리의 길이 상한과는 무관하지만, **판의 보상음(`perfect`)보다 크거나 길지 않는다** -
 * 봉투가 판보다 요란하면 사람은 판을 봉투를 여는 수단으로 읽고, 그 순간 이 게임의 무게가 뒤집힌다.
 * `sounds.test.ts`의 "perfect가 가장 크고 길다"가 그 선을 지킨다.
 */

/**
 * 봉투가 아래에서 올라와 자리를 잡는다(0~300ms). 종이가 스치는 소리라 노이즈가 주인공이고,
 * 로우패스가 위로 열리면서 "올라온다"는 방향을 만든다. 사인파 한 겹이 그 아래 몸통이다.
 */
export function envelopeOpen(): SoundSpec {
  const { hz, sec, gain } = AUDIO_TUNING.envelopeOpen
  return spec('envelopeOpen', gain, [
    noise(hz * 0.25, sec, 0.22, { kind: 'lowpass', hzTo: hz }),
    tone(220, sec * 0.8, 0.14, { wave: 'sine', hzTo: 330 }),
  ])
}

/**
 * 봉인이 찢긴다(1100ms). 하이패스 노이즈가 종이 섬유이고, 좁은 밴드패스 한 겹이 갈라지는 순간의 "딱"이다.
 * 낮은 사인파가 손이 당기는 무게를 준다. 셋이 다 있어야 찢는 것이고, 하나면 그냥 '치익'이다.
 */
export function envelopeTear(): SoundSpec {
  const { hz, sec, gain } = AUDIO_TUNING.envelopeTear
  return spec('envelopeTear', gain, [
    noise(hz, sec, 0.3, { kind: 'highpass' }),
    noise(hz * 0.6, 0.05, 0.18, { q: 4, delay: 0.04 }),
    tone(140, 0.08, 0.12, { wave: 'sine', hzTo: 90 }),
  ])
}

/**
 * 카드가 뒤집힌다(1100~1500ms 사이). 종이 한 장이 공기를 치는 소리 - 밴드패스 노이즈가 앞면, 위로 올라가는
 * 짧은 삼각파가 뒤집히는 방향, 뒤따르는 로우패스 노이즈가 뒷면이 자리에 놓이는 순간이다.
 */
export function cardFlip(): SoundSpec {
  const { hz, sec, gain } = AUDIO_TUNING.cardFlip
  return spec('cardFlip', gain, [
    noise(3000, 0.03, 0.2, { q: 3 }),
    tone(hz, sec, 0.14, { hzTo: hz * 1.5 }),
    noise(1200, 0.05, 0.12, { kind: 'lowpass', delay: 0.05 }),
  ])
}

/**
 * SR 이상이 나왔다. G5에서 5도 위로 한 번 열리는 2음 + 하이패스 반짝임.
 * `roundClear`(3음 상승)와 방향은 같되 한 음 적다 - 봉투는 판의 성공보다 작은 사건이다.
 */
export function gradeRare(): SoundSpec {
  const { hz, sec, gain } = AUDIO_TUNING.gradeRare
  return spec('gradeRare', gain, [
    tone(hz, 0.18, 0.24),
    tone(hz * 1.5, sec, 0.26, { wave: 'square', delay: 0.11 }),
    noise(6000, 0.2, 0.08, { kind: 'highpass', delay: 0.11, attack: 0.02 }),
  ])
}

/**
 * SSR. `perfect`와 같은 C 장조 아르페지오를 **한 옥타브 안에서** 끝낸다(C5 E5 G5 C6).
 * 위아래로 벌리는 방식(낮은 C4 + 하이패스 반짝임)은 그대로 빌려 오되, 두 옥타브를 오르지 않고
 * 게인도 낮다 - SSR 봉투가 퍼펙트보다 크게 울리면 사람은 봉투를 열려고 판을 돈다.
 */
export function gradeLegend(): SoundSpec {
  const { hz, sec, gain } = AUDIO_TUNING.gradeLegend
  return spec('gradeLegend', gain, [
    tone(hz, 0.14, 0.2),
    tone(hz * 1.26, 0.14, 0.2, { delay: 0.07 }),
    tone(hz * 1.5, 0.14, 0.2, { delay: 0.14 }),
    tone(hz * 2, sec, 0.26, { wave: 'square', delay: 0.21 }),
    tone(hz * 0.5, sec, 0.16, { wave: 'sine', delay: 0.21 }),
    noise(5000, 0.26, 0.1, { kind: 'highpass', delay: 0.21, attack: 0.02 }),
  ])
}

/** 차오름의 틱 수와 간격. 마지막 틱이 `perfect`보다 먼저 끝나야 한다(`sounds.test.ts`의 "perfect가 가장 길다"). */
const CHARGE_TICKS = 7
const CHARGE_GAP_SEC = 0.11

/**
 * 개봉 전의 차오름. 반음씩 오르는 짧은 틱 일곱.
 *
 * 겹을 시차로 쌓는 이유는 **정점이 겹치지 않게** 하기 위해서다 - 틱 하나의 길이(0.05초)가 간격(0.11초)보다
 * 짧으므로 어느 순간에도 살아 있는 겹은 하나뿐이고, 그래서 일곱 겹을 쌓아도 `perfect`보다 커지지 않는다.
 * 마지막 틱은 0.71초(= 6*0.11 + 0.05)에 끝난다.
 *
 * 그 끝이 **릴이 멎는 순간과 같은 순간**이 되도록 화면이 `envelopeChargeSec()`으로 역산해 앉힌다 -
 * 똑딱의 간격이 벌어지는 동안 음이 올라가고, 마지막 음과 정지가 함께 온다. 그 뒤의 침묵이 "숨 한 박자"이고,
 * 그 침묵을 소리로 채우면 정지가 정지로 안 들린다.
 */
export function envelopeCharge(): SoundSpec {
  const { hz, sec, gain } = AUDIO_TUNING.envelopeCharge
  const ticks: Voice[] = []
  for (let i = 0; i < CHARGE_TICKS; i += 1) {
    ticks.push(
      tone(hz * SEMITONE_RATIO ** i, sec, 0.22, { wave: 'triangle', delay: i * CHARGE_GAP_SEC }),
    )
  }
  return spec('envelopeCharge', gain, ticks)
}

/**
 * SSR의 금빛이 터진다(등급이 드러나는 순간). 낮은 사인파 몸통 + 위로 열리는 글라이드 + 하이패스 광채.
 * `envelopeImpact`와 `gradeLegend`가 앞뒤로 겹쳐 세 겹이 된다 - 겹의 수가 곧 등급이다(P5).
 */
export function envelopeBurst(): SoundSpec {
  const { hz, sec, gain } = AUDIO_TUNING.envelopeBurst
  return spec('envelopeBurst', gain, [
    tone(hz, sec, 0.24, { wave: 'sine', hzTo: hz * 0.5 }),
    tone(hz * 2, 0.3, 0.16, { hzTo: hz * 3 }),
    noise(5200, 0.34, 0.12, { kind: 'highpass', attack: 0.015 }),
  ])
}

/**
 * SR·SSR의 상승음. 낮은 톱니가 세 옥타브를 타고 오르고, 하이패스 노이즈가 그 위에서 함께 열린다.
 * 마지막 겹(사인)은 아래를 받치는 몸통이라 같이 오르되 한 옥타브만 간다.
 *
 * 어택을 길게 준 이유는 이 소리가 **시작을 알리지 않기** 때문이다 - 어둠과 함께 스며들어야 하고,
 * 첫 프레임에 정점을 치면 그건 상승음이 아니라 또 하나의 타격이다.
 */
export function envelopeRiser(): SoundSpec {
  const { hz, sec, gain } = AUDIO_TUNING.envelopeRiser
  return spec('envelopeRiser', gain, [
    tone(hz, sec, 0.2, { wave: 'sawtooth', hzTo: hz * 8, attack: 0.12 }),
    noise(hz * 4, sec, 0.12, { kind: 'highpass', hzTo: hz * 16, attack: 0.16 }),
    tone(hz * 0.5, sec, 0.16, { wave: 'sine', hzTo: hz, attack: 0.1 }),
  ])
}

/**
 * 카드가 꽂히는 저역 타격. 아래로 떨어지는 사인 몸통 + 로우패스 노이즈의 바람 + 아주 짧은 밴드패스 딱.
 *
 * 셋 중 무엇 하나를 빼도 소리가 남지만 **몸통을 빼면 타격이 아니다** - 이 소리의 일은 귀가 아니라
 * 가슴이고, 그래서 대역이 사전에서 가장 낮다.
 */
export function envelopeImpact(): SoundSpec {
  const { hz, sec, gain } = AUDIO_TUNING.envelopeImpact
  return spec('envelopeImpact', gain, [
    tone(hz, sec, 0.3, { wave: 'sine', hzTo: hz * 0.45 }),
    noise(hz * 6, 0.2, 0.14, { kind: 'lowpass' }),
    noise(hz * 20, 0.04, 0.1, { q: 3 }),
  ])
}

/**
 * 차오름의 총 길이(초). **화면이 이 값을 역산해 쓴다** - 릴이 멎는 순간에 이 소리가 끝나야
 * 마지막 접근의 "음이 올라간다"가 정지와 맞물린다(`ui/envelopePhases.ts`).
 *
 * 상수가 아니라 함수인 이유는 `AUDIO_TUNING`이 콘솔에서 바뀌기 때문이다 - 모듈 초기화 때 굳혀 두면
 * 길이를 바꾼 뒤에도 화면이 옛 값으로 시각을 잡는다.
 */
export function envelopeChargeSec(): number {
  return (CHARGE_TICKS - 1) * CHARGE_GAP_SEC + AUDIO_TUNING.envelopeCharge.sec
}

/**
 * 릴이 확률 띠의 경계 하나를 지난다. 좁은 밴드패스 노이즈 한 겹이 "딱"이고, 아래로 살짝 떨어지는
 * 삼각파 한 겹이 그 몸통이다. 겹이 둘뿐인 이유는 열몇 번 반복되는 소리이기 때문이다 -
 * 겹이 늘면 반복이 소음이 된다.
 */
export function reelTick(): SoundSpec {
  const { hz, sec, gain } = AUDIO_TUNING.reelTick
  return spec('reelTick', gain, [
    noise(hz * 2, 0.018, 0.14, { q: 5 }),
    tone(hz, sec, 0.1, { hzTo: hz * 0.8 }),
  ])
}

/* --- 조회 ----------------------------------------------------------------------- */

export const SOUND_NAMES = [
  'hit',
  'miss',
  'alreadyFound',
  'reload',
  'warpIn',
  'warpOut',
  'lowAmmo',
  'phaseChange',
  'roundClear',
  'roundFail',
  'perfect',
  'reveal',
  'uiTap',
  'someoneFound',
  'sniped',
  'ranking',
  'changeAccepted',
  'doorOpen',
  'envelopeOpen',
  'envelopeTear',
  'cardFlip',
  'gradeRare',
  'gradeLegend',
  'envelopeCharge',
  'envelopeBurst',
  'envelopeRiser',
  'envelopeImpact',
  'reelTick',
] as const

export type SoundName = (typeof SOUND_NAMES)[number]

/** 봉투 연출이 부르는 열. `ui/envelope.ts`가 이 이름만 받는다 - 사전에 없는 이름을 연출이 지어내지 못하게. */
export const ENVELOPE_SOUND_NAMES = [
  'envelopeOpen',
  'reelTick',
  'envelopeCharge',
  'envelopeRiser',
  'envelopeTear',
  'envelopeImpact',
  'cardFlip',
  'gradeRare',
  'gradeLegend',
  'envelopeBurst',
] as const satisfies readonly SoundName[]

export type EnvelopeSoundName = (typeof ENVELOPE_SOUND_NAMES)[number]

const BUILDERS: Readonly<Record<SoundName, (comboStep: number) => SoundSpec>> = {
  hit,
  miss,
  alreadyFound,
  reload,
  warpIn,
  warpOut,
  lowAmmo,
  phaseChange,
  roundClear,
  roundFail,
  perfect,
  reveal,
  uiTap,
  someoneFound,
  sniped,
  ranking,
  changeAccepted,
  doorOpen,
  envelopeOpen,
  envelopeTear,
  cardFlip,
  gradeRare,
  gradeLegend,
  envelopeCharge,
  envelopeBurst,
  envelopeRiser,
  envelopeImpact,
  reelTick,
}

/**
 * 이름으로 소리를 만든다. 호출부의 입구는 이것 하나다: `audio.play(sound('hit', combo))`.
 *
 * `comboStep`을 `hit` 아닌 소리가 무시하는 것은 의도다. 호출부가 "이 소리는 콤보를 받나"를
 * 기억해야 하면 언젠가 틀리고, 그 실수는 **소리가 안 나는** 형태로 조용히 나타난다.
 */
export function sound(name: SoundName, comboStep = 0): SoundSpec {
  return BUILDERS[name](comboStep)
}

/**
 * 리듀서의 `SoundId`(`game/gameState.ts`) → 이 사전의 이름.
 *
 * 타입을 직접 import 하지 않는다(값도 타입도). 사전이 게임 규칙에 묶이면 규칙 쪽 리팩터링
 * 하나가 소리 빌드를 깨고, 그건 **소리가 게임을 멈추는** 경로다 - 이 레이어가 절대 하면 안 되는 일이다.
 * 대가로 표가 자동으로 최신이 되지는 않으므로, `SoundId`가 늘면 여기에 한 줄을 손으로 더한다.
 * 빠뜨리면 그 소리만 나지 않고 게임은 그대로 돈다(조회 결과가 `undefined`다).
 *
 * `round-over`가 `roundClear`인 것은 **기본값일 뿐이다.** 실패로 끝난 라운드에는 호출부가
 * `roundFail`을, 무오답 전량 발견에는 `perfect`를 직접 골라야 한다 - 그 판단에 필요한
 * 정보(발견 수·오답 수)는 리듀서가 아니라 세션이 들고 있다.
 */
export const SOUND_FOR_GAME_ID: Readonly<Record<string, SoundName>> = {
  'shot-hit': 'hit',
  'shot-miss': 'miss',
  'shot-blocked': 'alreadyFound',
  'ammo-low': 'lowAmmo',
  'ammo-refill': 'reload',
  'warp-in': 'warpIn',
  'warp-out': 'warpOut',
  'phase-change': 'phaseChange',
  'round-over': 'roundClear',
}

/**
 * 매치 리듀서의 `PartySoundId`(`game/match/matchTypes.ts`) → 이 사전의 이름. `SOUND_FOR_GAME_ID`와 같은 규약이다 -
 * 타입을 가져오지 않고, 빠지면 그 소리만 나지 않는다. 표가 따로인 이유는 싱글 `SoundId` 표가 싱글의 것이라서다
 * (`matchTypes.PartySoundId` 주석). 셋이 새 소리다 - 남의 발견(`someoneFound`) · 늦은 한 발(`sniped`) · 순위 공개(`ranking`).
 * 관전 진입만 내 판이 끝난 소리(`roundFail`)를 다시 쓴다 - 관전은 그 사람에게는 라운드의 끝이다(4.3절).
 *
 * `ranking`을 내는 자리는 리듀서가 아니다(`PartySoundId`에는 있지만 `enterResult`는 `round-over`를 낸다). 매치 세션이
 * `round-over`를 이 표의 `ranking`으로 바꿔 부른다 - 여럿이의 라운드 끝은 성공·실패가 아니라 순위 공개라서다.
 */
export const SOUND_FOR_MATCH_ID: Readonly<Record<string, SoundName>> = {
  'other-found': 'someoneFound',
  sniped: 'sniped',
  'spectate-enter': 'roundFail',
  ranking: 'ranking',
}

/* --- 개발용 콘솔 핸들 ------------------------------------------------------------- */

/**
 * 귀로 튜닝하려면 **숫자를 바꾸고 곧바로 다시 들을 수 있어야** 한다. 빌드를 기다리는 순간
 * 하루에 시도할 수 있는 횟수가 두 자릿수로 떨어지고, 그러면 값은 관측이 아니라 취향으로 정해진다.
 *
 * 그래서 개발 모드에서만 콘솔에 손잡이를 하나 올린다. 화면을 한 번 탭한 뒤(오디오 해제):
 *
 * ```js
 * __wr.play('miss')          // 하나 듣기
 * __wr.play('hit', 7)        // 콤보 8단계의 발견음
 * __wr.all()                 // 전부 순서대로
 * __wr.tuning.miss.shellHz = 3000   // 탄피를 더 둔하게. 다음 재생부터 바로 적용된다
 * ```
 *
 * `import.meta.env.DEV` 안에 있으므로 배포 빌드에서는 통째로 사라진다.
 */
const DEV_HANDLE = '__wr'
/** `all()`이 소리 사이에 두는 간격(ms). 겹치면 무엇이 무엇인지 구별되지 않는다. */
const DEV_GAP_MS = 900

try {
  if (import.meta.env.DEV) {
    ;(globalThis as Record<string, unknown>)[DEV_HANDLE] = {
      engine: audio,
      tuning: AUDIO_TUNING,
      play: (name: SoundName, comboStep = 0): boolean => audio.play(sound(name, comboStep)),
      all: (): void => {
        SOUND_NAMES.forEach((name, index) => {
          setTimeout(() => {
            console.info(`[audio] ${name}`)
            audio.play(sound(name))
          }, index * DEV_GAP_MS)
        })
      },
    }
  }
} catch {
  // `import.meta.env`가 없는 실행기(순수 Node)도 있다. 콘솔 손잡이는 없어도 그만이다.
}
